import {
  AwardInputType,
  AwardType,
  DirtyFieldsType,
  Operation,
} from '@occc-app/offerHelpers/types';
import { getAwardInput } from '@occc-app/offerHelpers/utils/getAwardInput';
import { isFieldDirty } from '@occc-app/offerHelpers/utils';
import { makeDeleteOperation } from '@occc-app/offerHelpers/operations/makeDeleteOperation';
import { makeEarningMechanismCreateOperation } from '@occc-app/offerHelpers/operations/earningMechanism';
import { earningMechanismInputCreator } from '@occc-app/offerHelpers/earningMechanismInputCreator';
import { EarningMechanismConfigurationInput } from '@occc/graphql';

import { ChallengeConfigureFormValues, ProductChallengeFormValues } from '../../../types';
import { ChallengeType } from '../../../constants';

export type EarningMechanismCreateInput = Pick<
  EarningMechanismConfigurationInput,
  'visits' | 'varietyPurchase' | 'varietyPurchaseOverTime'
>;

type GetChallengeEarningMechanismCreateInput = (
  values: Exclude<ChallengeConfigureFormValues, { challengeType: null }>,
) => EarningMechanismCreateInput;

export const prepareProductChallengeCreateInput = (
  values: ProductChallengeFormValues,
  awardInput: AwardInputType,
) => {
  const { products = [], earningMechanism, stayVisibleWhileInProgress } = values;

  if (earningMechanism === 'varietyPurchase') {
    return earningMechanismInputCreator.createVarietyPurchase(products, awardInput);
  }

  return earningMechanismInputCreator.createVarietyPurchaseOverTime(
    products,
    awardInput,
    false,
    stayVisibleWhileInProgress?.isEnabled,
  );
};

export const getChallengeEarningMechanismCreateInput: GetChallengeEarningMechanismCreateInput = (
  values,
) => {
  const { challengeType, productChallenge, visitChallenge, award } = values;

  const awardInput = getAwardInput({
    awardType: award.awardType,
    awardVouchers: award.awardType === AwardType.AwardVouchers ? award.awardVouchers : undefined,
    awardPoints: award.awardType === AwardType.AwardPoints ? award.awardPoints : undefined,
  });

  if (challengeType === ChallengeType.ProductChallenge && productChallenge) {
    return prepareProductChallengeCreateInput(productChallenge, awardInput);
  }

  if (challengeType === ChallengeType.VisitChallenge && visitChallenge) {
    const { visitThreshold } = visitChallenge;
    return earningMechanismInputCreator.createVisits(visitThreshold, awardInput);
  }

  throw new Error(
    `${challengeType} challenge type or its configuration is not supported or not provided`,
  );
};

// Changing any of these fields deletes and recreates the earning mechanism,
// which resets accumulated progress for all members.
export const getIsEarningMechanismDirty = (dirtyFields: DirtyFieldsType): boolean =>
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'challengeType') ||
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'productChallenge') ||
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'visitChallenge') ||
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'award.awardType') ||
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'award.awardPoints') ||
  isFieldDirty<ChallengeConfigureFormValues>(dirtyFields, 'award.awardVouchers');

export const getEarningMechanismOperations = (
  offerId: string,
  values: ChallengeConfigureFormValues,
  dirtyFields: DirtyFieldsType,
): Operation[] => {
  const operations: Operation[] = [];

  if (!getIsEarningMechanismDirty(dirtyFields)) {
    return operations;
  }

  if (values.earningMechanism.id) {
    operations.push(makeDeleteOperation('earningMechanismDelete', values.earningMechanism.id));
  }

  if (!values.challengeType) {
    throw new Error('ChallengeType is not defined');
  }

  if (!values.award?.awardType) {
    throw new Error('AwardType is not defined');
  }

  const configuration = getChallengeEarningMechanismCreateInput(values);
  operations.push(makeEarningMechanismCreateOperation(offerId, configuration));

  return operations;
};
